import React, { useEffect, useRef, useMemo, useState } from 'react';
import './AISkinLoader.css';

const ANALYSIS_STEPS = [
  { id: 'quality', label: 'Image Quality Check', doneText: 'Perfect!' },
  { id: 'face', label: 'Face Detection', doneText: 'Complete' },
  { id: 'mapping', label: 'Facial Mapping', doneText: 'Complete' },
  {
    id: 'concerns',
    label: 'Skin Concerns Detection',
    doneText: 'Complete',
    activeText: 'Analyzing pigmentation, acne, redness & more…',
  },
  { id: 'texture', label: 'Skin Texture Analysis', doneText: 'Complete' },
  { id: 'interpret', label: 'AI Interpretation', doneText: 'Complete' },
  {
    id: 'report',
    label: 'Building Your Report',
    doneText: 'Complete',
    activeText: 'Preparing tips, routine & summary…',
  },
  {
    id: 'macro',
    label: 'Macro Close-up Analysis',
    doneText: 'Complete',
    activeText: 'Analyzing microscope close-ups…',
  },
];

const PHASE_TO_ACTIVE_STEP = {
  starting: 0,
  upload: 1,
  scan: 2,
  detect: 3,
  score: 4,
  finalize: 5,
  persist: 5,
  vision: 5,
  ai_bundle: 5,
  routine: 6,
  macro: 7,
  complete: 7,
  retry: 3,
};

function resolveActiveStepIndex(detectProgress) {
  const phase = detectProgress?.phase || 'starting';
  if (phase in PHASE_TO_ACTIVE_STEP) {
    return PHASE_TO_ACTIVE_STEP[phase];
  }
  const apiIndex = detectProgress?.stepIndex;
  if (typeof apiIndex === 'number') {
    return Math.min(ANALYSIS_STEPS.length - 1, Math.max(0, apiIndex));
  }
  return 0;
}

/** Progress stays inside the active checklist step until phase is complete. */
function resolveTargetPercent(detectProgress, stepElapsedMs) {
  if (!detectProgress) return 5;
  if (detectProgress.phase === 'complete') return 100;

  const stepIndex = resolveActiveStepIndex(detectProgress);
  const total = ANALYSIS_STEPS.length;
  const band = 100 / total;
  const floor = stepIndex * band;
  const phase = detectProgress.phase || '';
  const stepDurationMs = (
    phase === 'vision' || phase === 'ai_bundle' ? 30000
      : phase === 'routine' || phase === 'macro' ? 24000
        : phase === 'persist' ? 8000
          : 9000
  );
  const t = Math.min(1, Math.max(0, stepElapsedMs / stepDurationMs));
  const intra = 0.18 + t * 0.72;
  const value = floor + band * intra;

  return Math.min(99, Math.max(4, Math.round(value)));
}

function parseCssHexColor(value) {
  const raw = String(value || '').trim();
  if (!raw.startsWith('#')) return null;
  const hex = raw.slice(1);
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  }
  if (hex.length >= 6) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  return null;
}

function getThemeAccentRgb() {
  if (typeof window === 'undefined') return { r: 158, g: 116, b: 54 };
  const style = getComputedStyle(document.documentElement);
  const keys = ['--cta-primary', '--skin-highlight', '--skin-text-secondary'];
  for (let i = 0; i < keys.length; i += 1) {
    const rgb = parseCssHexColor(style.getPropertyValue(keys[i]));
    if (rgb) return rgb;
  }
  return { r: 158, g: 116, b: 54 };
}

function StepStatusIcon({ status }) {
  if (status === 'done') {
    return (
      <span className="ai-loader-step-icon ai-loader-step-icon--done" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M8.5 12.2l2.4 2.4 5.1-5.4" />
        </svg>
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className="ai-loader-step-icon ai-loader-step-icon--active" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="3 4" />
        </svg>
      </span>
    );
  }
  return (
    <span className="ai-loader-step-icon ai-loader-step-icon--pending" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" strokeDasharray="2 4" opacity="0.45" />
      </svg>
    </span>
  );
}

const AISkinLoader = ({
  imageSrc,
  landmarks = null,
  progressMessage = null,
  detectProgress = null,
}) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timeRef = useRef(0);
  const dataPointsRef = useRef([]);
  const [displayPercent, setDisplayPercent] = useState(4);
  const stepIndexRef = useRef(0);
  const stepStartedAtRef = useRef(Date.now());

  const activeStepIndex = resolveActiveStepIndex(detectProgress);

  useEffect(() => {
    if (activeStepIndex === stepIndexRef.current) return;
    const band = 100 / ANALYSIS_STEPS.length;
    const floor = Math.round(activeStepIndex * band);
    setDisplayPercent((prev) => Math.max(prev, floor));
    stepIndexRef.current = activeStepIndex;
    stepStartedAtRef.current = Date.now();
  }, [activeStepIndex]);

  useEffect(() => {
    dataPointsRef.current = Array.from({ length: 12 }, () => ({
      x: Math.random(),
      y: Math.random(),
      size: Math.random() * 2.5 + 1,
      opacity: Math.random(),
    }));
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const stepElapsed = Date.now() - stepStartedAtRef.current;
      const target = resolveTargetPercent(detectProgress, stepElapsed);
      setDisplayPercent((prev) => {
        if (detectProgress?.phase === 'complete') return 100;
        if (prev >= target) return prev;
        const delta = target - prev;
        const step = delta > 10 ? Math.max(1, Math.round(delta / 5)) : 1;
        return Math.min(target, prev + step);
      });
    }, 140);
    return () => clearInterval(intervalId);
  }, [detectProgress]);

  const faceRegion = useMemo(() => {
    if (landmarks && landmarks.length > 0) {
      const xs = landmarks.map((l) => l.x);
      const ys = landmarks.map((l) => l.y);
      return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      };
    }
    return null;
  }, [landmarks]);

  const imageReadyRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const frame = document.querySelector('.ai-loader-background');
    const imgElement = document.querySelector('.ai-loader-image');
    if (!canvas || !imageSrc) return undefined;

    imageReadyRef.current = false;

    const syncCanvasSize = () => {
      const box = frame || imgElement;
      if (!box) return;
      const displayedWidth = box.clientWidth;
      const displayedHeight = box.clientHeight;
      if (displayedWidth > 0 && displayedHeight > 0) {
        canvas.width = displayedWidth;
        canvas.height = displayedHeight;
        canvas.style.width = `${displayedWidth}px`;
        canvas.style.height = `${displayedHeight}px`;
      }
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      syncCanvasSize();
      imageReadyRef.current = true;
    };
    img.src = imageSrc;

    const resizeObserver = new ResizeObserver(syncCanvasSize);
    if (frame) resizeObserver.observe(frame);
    else if (imgElement) resizeObserver.observe(imgElement);
    syncCanvasSize();

    return () => {
      img.onload = null;
      resizeObserver.disconnect();
    };
  }, [imageSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const accent = getThemeAccentRgb();
    const accentStr = `rgba(${accent.r}, ${accent.g}, ${accent.b}, 1)`;

    const checkAndAnimate = () => {
      if (!imageReadyRef.current || canvas.width === 0 || canvas.height === 0) {
        setTimeout(checkAndAnimate, 50);
        return;
      }

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      const animate = () => {
        timeRef.current += 0.016;
        const t = timeRef.current;

        // Overlay only — photo is the <img> underneath (avoids stacked double image).
        ctx.clearRect(0, 0, width, height);

        const region = faceRegion || {
          x: width * 0.2,
          y: height * 0.15,
          width: width * 0.6,
          height: height * 0.65,
        };

        const centerX = region.x + region.width / 2;
        const centerY = region.y + region.height / 2;
        const radius = Math.min(region.width, region.height) * 0.42;

        ctx.save();

        // Radial scan grid
        ctx.strokeStyle = `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0.22)`;
        ctx.lineWidth = 1;
        for (let ring = 1; ring <= 4; ring += 1) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, (radius / 4) * ring, 0, Math.PI * 2);
          ctx.stroke();
        }
        for (let i = 0; i < 8; i += 1) {
          const angle = (Math.PI / 4) * i + t * 0.15;
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius);
          ctx.stroke();
        }

        // Crosshair
        ctx.strokeStyle = `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0.55)`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(centerX - radius * 0.35, centerY);
        ctx.lineTo(centerX + radius * 0.35, centerY);
        ctx.moveTo(centerX, centerY - radius * 0.35);
        ctx.lineTo(centerX, centerY + radius * 0.35);
        ctx.stroke();

        // Corner brackets
        const bracketSize = 26;
        ctx.strokeStyle = accentStr;
        ctx.lineWidth = 2.5;
        ctx.lineJoin = 'miter';

        ctx.beginPath();
        ctx.moveTo(region.x, region.y + bracketSize);
        ctx.lineTo(region.x, region.y);
        ctx.lineTo(region.x + bracketSize, region.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(region.x + region.width - bracketSize, region.y);
        ctx.lineTo(region.x + region.width, region.y);
        ctx.lineTo(region.x + region.width, region.y + bracketSize);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(region.x, region.y + region.height - bracketSize);
        ctx.lineTo(region.x, region.y + region.height);
        ctx.lineTo(region.x + bracketSize, region.y + region.height);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(region.x + region.width - bracketSize, region.y + region.height);
        ctx.lineTo(region.x + region.width, region.y + region.height);
        ctx.lineTo(region.x + region.width, region.y + region.height - bracketSize);
        ctx.stroke();

        // Scan line
        const scanProgress = (t * 0.22) % 2;
        const scanY = scanProgress <= 1
          ? region.y + scanProgress * region.height
          : region.y + (2 - scanProgress) * region.height;

        const gradient = ctx.createLinearGradient(0, scanY - 24, 0, scanY + 24);
        gradient.addColorStop(0, `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0)`);
        gradient.addColorStop(0.5, `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0.75)`);
        gradient.addColorStop(1, `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0)`);
        ctx.fillStyle = gradient;
        ctx.fillRect(region.x, scanY - 24, region.width, 48);

        ctx.strokeStyle = accentStr;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 10;
        ctx.shadowColor = `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0.55)`;
        ctx.beginPath();
        ctx.moveTo(region.x, scanY);
        ctx.lineTo(region.x + region.width, scanY);
        ctx.stroke();
        ctx.shadowBlur = 0;

        ctx.restore();
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animate();
    };

    checkAndAnimate();

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [faceRegion, imageSrc]);

  const progressCaption = progressMessage || 'Analyzing your skin…';

  return (
    <div className="ai-skin-loader">
      <div className="ai-loader-layout">
        <div className="ai-loader-preview-col">
          <div className="ai-loader-background">
            {imageSrc ? (
              <img src={imageSrc} alt="Skin analysis in progress" className="ai-loader-image" />
            ) : null}
            <canvas ref={canvasRef} className="ai-loader-canvas" />
          </div>
        </div>

        <div className="ai-loader-stage-col">
          <div className="ai-loader-progress-panel" aria-live="polite">
            <div className="ai-loader-percent">{displayPercent}%</div>
            <p className="ai-loader-progress-caption">{progressCaption}</p>
            <div
              className="ai-loader-progress-bar"
              role="progressbar"
              aria-valuenow={displayPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Analysis progress"
            >
              <span className="ai-loader-progress-bar-fill" style={{ width: `${displayPercent}%` }} />
            </div>
            <div className="ai-loader-info-card">
              <span className="ai-loader-info-icon" aria-hidden="true">✦</span>
              <p>
                {detectProgress?.phase === 'macro'
                  ? 'Running microscope close-up analysis — almost ready.'
                  : detectProgress?.phase === 'ai_bundle' || detectProgress?.phase === 'routine'
                    ? 'Personalizing your report with AI — usually under a minute.'
                    : 'Our AI is scanning multiple skin layers. This may take a few seconds.'}
              </p>
            </div>
          </div>

          <div className="ai-loader-steps-col">
            <h3 className="ai-loader-steps-title">Analysis in progress</h3>
            <ol className="ai-loader-steps-list">
              {ANALYSIS_STEPS.map((step, index) => {
                const status = index < activeStepIndex
                  ? 'done'
                  : index === activeStepIndex
                    ? 'active'
                    : 'pending';
                const detail = status === 'done'
                  ? step.doneText
                  : status === 'active'
                    ? (step.activeText || progressCaption)
                    : 'Pending';

                return (
                  <li
                    key={step.id}
                    className={`ai-loader-step ai-loader-step--${status}`}
                  >
                    <div className="ai-loader-step-body">
                      <span className="ai-loader-step-label">{step.label}</span>
                      <span className="ai-loader-step-detail">{detail}</span>
                    </div>
                    <StepStatusIcon status={status} />
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>

      <section className="ai-loader-did-you-know" aria-label="Did you know">
        <div className="ai-loader-dyk-intro">
          <span className="ai-loader-dyk-icon" aria-hidden="true">💡</span>
          <div>
            <h4>Did you know?</h4>
            <p>We analyze 15+ skin parameters using advanced AI to give you the most accurate skin insights.</p>
          </div>
        </div>
        <div className="ai-loader-dyk-cards">
          <div className="ai-loader-dyk-card">
            <span className="ai-loader-dyk-card-icon" aria-hidden="true">◎</span>
            <strong>AI powered</strong>
            <span>Advanced AI models for accurate analysis</span>
          </div>
          <div className="ai-loader-dyk-card">
            <span className="ai-loader-dyk-card-icon" aria-hidden="true">🛡</span>
            <strong>100% private</strong>
            <span>Your photos are secure and never shared</span>
          </div>
          <div className="ai-loader-dyk-card">
            <span className="ai-loader-dyk-card-icon" aria-hidden="true">⏱</span>
            <strong>Fast &amp; precise</strong>
            <span>Detailed results in just a few seconds</span>
          </div>
        </div>
      </section>

      <p className="ai-loader-privacy-footer">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        Your data is safe &amp; private
      </p>
    </div>
  );
};

export default AISkinLoader;
